// frontend/src/modules/invoicing/pages/ClientDetailPage.tsx
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, FileText, ArrowUpRight } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatDate, formatMoney } from '../../../design-system/format';
import { VISUAL } from '../../../design-system/visual';
import { StatusBadge } from '../components/StatusBadge';
import { clientsApi } from '../api';
import { amountDue } from '../types';
import type { Client, Invoice } from '../types';

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function avatarBgFor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return `oklch(0.88 0.07 ${h})`;
}

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';
  const confirm = useConfirm();

  const clientQ = useApi<{ client: Client }>(id ? `/api/invoicing/clients/${id}` : null);
  const invoicesQ = useApi<{ invoices: Invoice[] }>(id ? `/api/invoicing/invoices?client_id=${id}` : null);

  if (clientQ.loading) return <LoadingState variant="page" />;
  if (!clientQ.data) return <div className="p-10 text-[14px] text-destructive">{clientQ.error ?? 'Client not found.'}</div>;
  const client = clientQ.data.client;
  const invoices = invoicesQ.data?.invoices ?? [];
  // Money math counts REAL invoices only — estimates are quotes and credit
  // notes are reversals, so neither belongs in "Billed". Outstanding covers
  // every open payable state (sent / partially paid / overdue) and, for
  // partials, only the remaining balance (total − paid − credited).
  const realInvoices = invoices.filter((inv) => (inv.document_type ?? 'invoice') === 'invoice');
  const totalBilled = realInvoices.reduce((s, inv) => s + Number(inv.total || 0), 0);
  const outstanding = realInvoices
    .filter((i) => i.status === 'sent' || i.status === 'partial' || i.status === 'overdue')
    .reduce((s, inv) => s + amountDue(inv), 0);

  async function remove() {
    if (!id) return;
    if (!(await confirm({ title: `Delete ${client.name}?`, description: 'Their existing invoices stay.', destructive: true, confirmLabel: 'Delete' }))) return;
    try {
      await clientsApi.remove(id);
      toast.success('Client deleted');
      navigate('/dashboard/clients');
    } catch (err: any) {
      toast.error('Delete failed', { description: err.message });
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow={<><Link to="/dashboard/clients" className="hover:text-foreground transition-colors">Clients</Link> / {client.name}</>}
        title={client.name}
        description={client.email ?? 'No email on file'}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={remove} className="rounded-full h-10 px-3 text-[13px] font-semibold gap-1.5 text-muted-foreground hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
            <Link to={`/dashboard/clients/${id}/edit`}>
              <Button variant="outline" className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Button>
            </Link>
            <Link to={`/dashboard/invoices/new?client=${id}`}>
              <Button className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                New invoice
              </Button>
            </Link>
          </div>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6 max-w-5xl">
        {/* Hero card — avatar + identity + KPIs */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 110, damping: 22 }}
          className={[VISUAL.bento, 'p-7 grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-6 items-center'].join(' ')}
        >
          <div
            className="h-16 w-16 rounded-2xl ring-1 ring-border/40 flex items-center justify-center text-[18px] font-bold text-foreground/85 shrink-0"
            style={{ background: avatarBgFor(client.name) }}
            aria-hidden
          >
            {initialsFor(client.name)}
          </div>
          <div className="min-w-0">
            <p className={[VISUAL.eyebrow, 'mb-1'].join(' ')}>Client</p>
            <p className="font-sans font-semibold text-[22px] tracking-[-0.02em] text-foreground truncate">{client.name}</p>
            <p className="text-[12.5px] text-muted-foreground mt-0.5 truncate">
              Added {formatDate(client.created_at)}
              {client.tax_id ? ` · Tax ${client.tax_id}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <MiniKpi label="Billed" value={formatMoney(totalBilled, currency)} caption={`${realInvoices.length} invoice${realInvoices.length === 1 ? '' : 's'}`} />
            <MiniKpi label="Outstanding" value={formatMoney(outstanding, currency)} caption="Open" tone="saffron" />
          </div>
        </motion.section>

        {/* Contact details */}
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Contact</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <DetailRow label="Email"   value={client.email} />
            <DetailRow label="Phone"   value={client.phone} />
            <DetailRow label="Address" value={client.address} />
            <DetailRow label="Billing" value={client.billing_address ?? client.address} />
            <DetailRow label="Tax ID"  value={client.tax_id} />
            <DetailRow label="Added"   value={formatDate(client.created_at)} />
          </dl>
          {client.notes && (
            <div className="mt-6 pt-5 border-t border-border/40">
              <p className={[VISUAL.eyebrow, 'mb-2'].join(' ')}>Internal notes</p>
              <p className="text-[13.5px] text-foreground whitespace-pre-wrap leading-relaxed">{client.notes}</p>
            </div>
          )}
        </section>

        {/* Invoices */}
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h3 className={VISUAL.dashSection}>Invoices</h3>
            <Link to={`/dashboard/invoices/new?client=${id}`} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-foreground hover:text-saffron-foreground transition-colors">
              <Plus className="h-3 w-3" /> New
            </Link>
          </div>
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            {invoicesQ.loading ? (
              <RowSkeleton />
            ) : invoices.length === 0 ? (
              <EmptyInvoicesForClient clientId={id ?? ''} />
            ) : (
              <ul className="divide-y divide-border/40">
                {invoices.map((inv, i) => (
                  <motion.li
                    key={inv.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.02, 0.18) }}
                  >
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/invoices/${inv.id}`)}
                      className="w-full text-left flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-muted/40 transition-colors"
                    >
                      <div className="h-10 w-10 rounded-xl bg-muted/50 ring-1 ring-border/40 flex items-center justify-center shrink-0">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.8} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-[12.5px] font-mono text-muted-foreground">{inv.invoice_number}</span>
                        </div>
                        <p className="text-[12px] text-muted-foreground mt-0.5">
                          Issued {formatDate(inv.issued_at)} · Due {formatDate(inv.due_at)}
                        </p>
                      </div>
                      <div className="hidden sm:flex flex-col items-end gap-1.5">
                        <StatusBadge status={inv.status} />
                        <span className="text-[14px] font-semibold tabular-nums text-foreground">
                          {formatMoney(inv.total, currency)}
                        </span>
                      </div>
                    </button>
                  </motion.li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className={VISUAL.eyebrow}>{label}</dt>
      <dd className="text-[13.5px] text-foreground mt-1 whitespace-pre-wrap">
        {value || <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}

function MiniKpi({ label, value, caption, tone = 'neutral' }: { label: string; value: React.ReactNode; caption: string; tone?: 'neutral' | 'saffron' }) {
  return (
    <div className={[
      'rounded-2xl ring-1 px-4 py-3 min-w-[120px]',
      tone === 'saffron' ? 'bg-saffron/10 ring-saffron/30' : 'bg-muted/40 ring-border/40',
    ].join(' ')}>
      <p className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-muted-foreground/80">{label}</p>
      <p className="font-sans font-semibold text-[18px] tabular-nums tracking-[-0.02em] text-foreground mt-0.5">{value}</p>
      <p className="text-[10.5px] text-muted-foreground mt-0.5">{caption}</p>
    </div>
  );
}

function RowSkeleton() {
  return (
    <ul className="divide-y divide-border/40">
      {Array.from({ length: 3 }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="h-10 w-10 rounded-xl bg-muted/60 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 rounded bg-muted/60 animate-pulse" />
            <div className="h-2.5 w-1/2 rounded bg-muted/40 animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyInvoicesForClient({ clientId }: { clientId: string }) {
  return (
    <div className="py-12 px-8 text-center">
      <p className="text-[14px] font-semibold text-foreground">No invoices yet</p>
      <p className="text-[13px] text-muted-foreground mt-1 mb-5">Send their first one and it'll appear here.</p>
      <Link to={`/dashboard/invoices/new?client=${clientId}`}>
        <Button className="rounded-full h-10 px-5 font-semibold gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Create invoice <ArrowUpRight className="h-3.5 w-3.5" />
        </Button>
      </Link>
    </div>
  );
}
